/**
 * Toast — transient feedback
 *
 * The shared success/error surface called for by design-system.md §2.2 and §3
 * ("Inline feedback"). Hand-rolled rather than Radix Toast: no @radix-ui/*
 * package is installed, and a queue this small doesn't justify adding one.
 *
 * Why it exists: pages were reporting outcomes either by growing their own
 * ad-hoc error banner, or — worse — not at all, as with the optimistic module
 * toggles that failed silently. A write that quietly doesn't happen is the
 * worst thing an admin page can do, so feedback belongs in one place.
 *
 * Usage:
 *   const { success, error } = useToast()
 *   success('Availability updated')
 *   error('Could not update availability')
 *
 * Mounted once in main.tsx, above <App/>, so ANY page can call useToast() —
 * including unauthenticated ones outside the Layout shell, such as
 * /admin/break-glass. (It lived in Layout.tsx until R16b, which made the
 * emergency page the only page that could not report a failure.) Errors linger
 * longer than successes and are announced
 * assertively; every toast can be dismissed, and hovering pauses its timer so
 * a message can't vanish mid-read.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import clsx from 'clsx'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastMessage {
  id: number
  message: string
  variant: ToastVariant
  /** Milliseconds before auto-dismiss. 0 or less = sticky until dismissed. */
  duration: number
}

interface ToastContextValue {
  /** Show a toast. Returns its id, so a caller can dismiss it early. */
  toast: (message: string, variant?: ToastVariant, duration?: number) => number
  success: (message: string) => number
  error: (message: string) => number
  warning: (message: string) => number
  info: (message: string) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/**
 * Errors sit longer: they carry more text, and they're the ones worth reading.
 */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 7000,
  error: 8000,
}

/** Past this the oldest is dropped, so a burst of failures can't bury the page. */
const MAX_VISIBLE = 4

/**
 * Access the toast queue.
 *
 * Throws outside a provider rather than no-opping: a silent no-op here would
 * reintroduce exactly the failure mode this component exists to remove.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used inside a <ToastProvider>')
  }
  return ctx
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info', duration?: number) => {
      const id = nextId.current++
      setToasts((prev) =>
        [
          ...prev,
          {
            id,
            message,
            variant,
            duration: duration ?? DEFAULT_DURATION[variant],
          },
        ].slice(-MAX_VISIBLE),
      )
      return id
    },
    [],
  )

  // Stable identity: consumers put these in useEffect/useCallback deps.
  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (message: string) => toast(message, 'success'),
      error: (message: string) => toast(message, 'error'),
      warning: (message: string) => toast(message, 'warning'),
      info: (message: string) => toast(message, 'info'),
      dismiss,
    }),
    [toast, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

const VARIANT_STYLES: Record<
  ToastVariant,
  { wrapper: string; icon: React.ElementType; iconClass: string }
> = {
  success: {
    wrapper: 'bg-state-success-bg border-state-success/40',
    icon: CheckCircle2,
    iconClass: 'text-state-success',
  },
  error: {
    wrapper: 'bg-state-danger-bg border-state-danger/40',
    icon: AlertCircle,
    iconClass: 'text-state-danger',
  },
  warning: {
    wrapper: 'bg-state-warning-bg border-state-warning/40',
    icon: AlertTriangle,
    iconClass: 'text-state-warning',
  },
  info: {
    wrapper: 'bg-state-info-bg border-state-info/40',
    icon: Info,
    iconClass: 'text-state-info',
  },
}

/**
 * The stack itself. Above the sidebar (z-50) and its mobile scrim (z-40), so a
 * toast is never hidden behind the nav on a small screen.
 */
function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastMessage[]
  onDismiss: (id: number) => void
}) {
  if (toasts.length === 0) return null

  return (
    <div
      className="fixed z-[60] bottom-4 right-4 left-4 sm:left-auto sm:w-96 flex flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastMessage
  onDismiss: (id: number) => void
}) {
  const [paused, setPaused] = useState(false)
  const { wrapper, icon: Icon, iconClass } = VARIANT_STYLES[toast.variant]

  // Unpausing restarts the full duration rather than resuming the remainder.
  // Deliberate: tracking elapsed time would need a second ref and a clock read
  // per render, and erring towards "on screen longer" is the safe direction.
  useEffect(() => {
    if (paused || toast.duration <= 0) return
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.duration)
    return () => window.clearTimeout(timer)
  }, [paused, toast.duration, toast.id, onDismiss])

  return (
    <div
      // Errors interrupt; everything else waits its turn in the polite region.
      role={toast.variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={clsx(
        'flex items-start gap-3 p-3 rounded-lg border shadow-e2 text-sm text-text-primary',
        wrapper,
      )}
    >
      <Icon size={18} className={clsx('shrink-0 mt-px', iconClass)} />
      <span className="flex-1 leading-snug break-words">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 p-0.5 rounded text-text-muted hover:text-text-primary"
      >
        <X size={16} />
      </button>
    </div>
  )
}

export default ToastProvider
